import { useState, useCallback, useMemo } from 'react'
import { CardListProps } from '../types'
import { NewSourceCardItem } from './source-card-item-reimplementation/new-source-card-item'
import { useGlobalSourceManagementStore } from '@/stores/globalSourceManagementStore'

export const SourceCardList = ({ cards, messageId }: CardListProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const globalSourceContext = useGlobalSourceManagementStore();

  const groupedCards = useMemo(() => {
    const validCards = cards.filter(card => card.attributes?.fileId);
    
    if (validCards.length !== cards.length) {
      validCards.push(...cards.filter(card => !card.attributes?.fileId));
    }
    
    const groups = validCards.reduce((acc, card) => {
      const fileId = card.attributes?.fileId || card.id
      const resultType = card.attributes?.type || 'document'
      const groupKey = `${fileId}:${resultType}`

      if (!acc[groupKey]) {
        acc[groupKey] = []
      }
      acc[groupKey].push(card)
      return acc
    }, {} as Record<string, typeof cards>)
    
    const result = Object.entries(groups).map(([groupKey, groupCards], index) => {
      const [fileId] = groupKey.split(':')

      return {
        uniqueId: `${groupKey}-${index}`,
        fileId,
        cards: groupCards,
        primaryCard: groupCards[0],
        count: groupCards.length
      }
    });
    
    return result;
  }, [cards])

  const handleClose = useCallback(() => {
    setSelectedId(null)
    globalSourceContext.inactiveCurrentSource();
  }, [globalSourceContext])

  const handleSelect = useCallback((id: string, sourceId: string | undefined) => {
    setSelectedId(id)
    if(sourceId) {
      globalSourceContext.setActiveSource(messageId, sourceId, 0);
    }
  }, [globalSourceContext, messageId])

  return (
    <ul className='mx-auto flex max-w-[700px] flex-col items-center justify-center gap-1'>
      {groupedCards.map((group, idx) => {
        const cardsIncludeSourceId = group.cards.some((card) => card.attributes?.sourceId === globalSourceContext.activeSourceId);
        const isSelected = 
          (selectedId === group.primaryCard.id) || 
          (cardsIncludeSourceId && globalSourceContext.activeSourceMessageId === messageId) ||
          (cardsIncludeSourceId);

        return (
          <NewSourceCardItem
            key={group.uniqueId}
            {...group.primaryCard}
            groupedCount={group.count > 1 ? group.count : undefined}
            groupedCards={group.cards}
            isSelected={isSelected}
            onClose={handleClose}
            onSelect={handleSelect}
            messageId={messageId}
            className={`${idx > 0 && "mt-2"}`}
          />
      )})}
    </ul>
  )
}
